import React from 'react';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../../components/SectionContainer';
import RevealOnScroll from '../../components/RevealOnScroll';

const STEPS = [
  {
    number: '01',
    title: 'Tell us about your institute',
    description: 'Fill the form. Takes 2 minutes.',
  },
  {
    number: '02',
    title: 'We verify and onboard',
    description:
      'Our team calls, verifies, and helps you set up your complete profile — gallery, courses, batches.',
  },
  {
    number: '03',
    title: 'Go live with a premium profile',
    description:
      'Your institute appears in city + course searches. Students start discovering you.',
  },
  {
    number: '04',
    title: 'Qualified leads roll in',
    description:
      'See every inquiry in your dashboard with full student details the moment it comes in.',
  },
];

function HowItWorksSection() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.5)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.07)'
    : '1px solid rgba(27,54,93,0.06)';
  const connectorColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(27,54,93,0.1)';

  return (
    <SectionContainer
      id="how-it-works"
      eyebrow="Simple onboarding"
      title="From signup to first lead in under a week."
      subtitle="We handle the setup so you can focus on enrolling students."
    >
      <Box sx={{ position: 'relative' }}>
        {/* Horizontal connector line on desktop */}
        {isDesktop && (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 28,
              left: '12.5%',
              right: '12.5%',
              height: 2,
              background: connectorColor,
              zIndex: 0,
            }}
          />
        )}

        <Grid container spacing={{ xs: 3, md: 3 }} alignItems="flex-start">
          {STEPS.map(({ number, title, description }, i) => (
            <Grid item xs={12} md={3} key={number}>
              <RevealOnScroll delay={i * 0.1}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 2, md: 0 },
                    textAlign: { xs: 'left', md: 'center' },
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {/* Step number circle */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${orange}, ${golden})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 0, md: 3 },
                      boxShadow: '0 8px 24px rgba(242,101,34,0.3)',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: '#fff', fontSize: '0.95rem' }}
                    >
                      {number}
                    </Typography>
                  </Box>

                  {/* Text block */}
                  <Box
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      borderRadius: '16px',
                      background: cardBg,
                      border: cardBorder,
                      width: '100%',
                      boxShadow: isDark
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(27,54,93,0.06)',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: 'text.primary', mb: 0.75, lineHeight: 1.3 }}
                    >
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      {description}
                    </Typography>
                  </Box>
                </Box>
              </RevealOnScroll>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SectionContainer>
  );
}

export default HowItWorksSection;
